import styled from 'styled-components';

import type { ReactNode, MouseEvent } from 'react';

import type { ThemeTypes } from 'types/Theme.namespace';
import { media } from 'helpers/breakpoints';
import { SaveAd } from 'components/Toolkit/SaveAd/SaveAd';
import { MediaCount } from 'components/Toolkit/Tags/MediaCount';
import { InfoTitle } from 'components/Toolkit/InfoTitle/InfoTitle';
import { PriceTypes } from 'components/AdForm/types';
import { IMerits } from 'types';

export type CardVariations = 'SIMPLE' | 'ENHANCED' | 'XS' | 'SMALL';
export type CardOrientation = 'HORIZONTAL' | 'VERTICAL';
const ImageDimensions = { width: 600, height: 450 };

type Highlights = ThemeTypes.Colors;
export interface CardProps {
  variant: CardVariations;
  orientation: CardOrientation;
  title?: string;
  keyInfoList?: string[];
  price?: string;
  currency?: 'EUR' | 'GBP';
  oldPrice?: string;
  imgSrc?: string;
  imgSrcWebp?: string;
  noImgSrc: string;
  imgAlt?: string;
  userSaved?: boolean;
  onSave?: (event: MouseEvent) => void;
  highlight?: Highlights;
  highlightText?: string;
  info?: { title: string; content: string; subText?: string }[];
  infoLabel?: string;
  mediaCount?: number;
  header?: {
    title?: string;
    subTitle?: string | ReactNode;
    logo?: string;
  };
  meritsV2?: IMerits[];
  tags?: ReactNode[];
  priceOnRequest?: boolean;
  renderAdditionalHeaderInfo?: ReactNode;
  shouldTruncateHeader?: boolean;
  smallTruncationHeaderWidth?: number;
}

const CardWrapper = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.default};
  box-shadow: ${({ theme }) => theme.shadow.CONTAINER};
  overflow: hidden;
  height: 100%;

  &.orientation-HORIZONTAL {
    height: initial;
  }
`;

const Header = styled.div`
  background: ${({ theme }) => theme.colors.WHITE};
  padding: ${({ theme }) => `${theme.spacing.S8} ${theme.spacing.M16}`};
  display: flex;
  align-items: center;
`;

const Highlight = styled.p<{ highlight?: Highlights }>`
  ${({ theme }) => theme.fontSize.B12}
  position: absolute;
  top: 0;
  right: 0;
  padding: ${({ theme }) => `${theme.spacing.S2} ${theme.spacing.S8}`};
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius.default};
  z-index: 10;

  ${({ theme, highlight }) => {
    if (highlight) {
      return `
        background-color: ${theme.colors[highlight]};
        color: ${theme.colors.WHITE};
        text-transform: uppercase;
      `;
    }
  }};
`;

const MainContent = styled.div<{ highlight?: Highlights; showBorder: boolean }>`
  position: relative;
  ${({ theme, highlight, showBorder }) =>
    highlight
      ? `border-top: 2px solid ${theme.colors[highlight]}`
      : showBorder && `border-top: 1px solid ${theme.colors.GREY_LIGHT}`};

  height: 100%;
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.GREY_DARKER};

  .orientation-HORIZONTAL & {
    ${media.medium} {
      min-height: 240px;
      flex-direction: row;
    }
  }
`;

export const Title = styled.p`
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  ${({ theme }) => theme.fontSize.L18};
  position: relative;
  max-height: 48px;
  overflow: hidden;
  word-break: break-word;

  .variant-SIMPLE & {
    ${media.smallOnly} {
      ${({ theme }) => theme.fontSize.B12};
      max-height: 32px;
    }
  }

  .variant-XS & {
    ${({ theme }) => theme.fontSize.B12};
    max-height: 32px;
  }
`;

const KeyInfoItem = styled.li`
  ${({ theme }) => theme.fontSize.M14};
  color: ${({ theme }) => theme.colors.GREY_DARK};
  list-style: none;
  display: flex;
  align-items: center;

  .variant-XS & {
    ${({ theme }) => theme.fontSize.B12};
  }

  .variant-SIMPLE & {
    ${media.smallOnly} {
      ${({ theme }) => theme.fontSize.B12};
    }
  }

  &:after {
    width: 4px;
    height: 4px;
    background: ${({ theme }) => theme.colors.GREY};
    margin-left: ${({ theme }) => theme.spacing.S8};
    margin-right: ${({ theme }) => theme.spacing.S8};
    content: '';
    border-radius: 50%;
  }

  &:last-child {
    &:after {
      width: ${({ theme }) => theme.spacing.S4};
      content: none;
      height: 100%;
    }
  }
`;

const KeyInfoList = styled.ul`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.S4};

  .variant-SIMPLE.orientation-HORIZONTAL & {
    ${media.smallOnly} {
      ${KeyInfoItem}:first-child {
        display: none;
      }
    }
  }

  .variant-ENHANCED.orientation-VERTICAL & {
    ${media.smallOnly} {
      ${KeyInfoItem} {
        display: none;
      }
    }
  }

  .variant-XS & {
    ${KeyInfoItem}:first-child {
      display: none;
    }
  }
`;

const SimpleSaveAd = styled(SaveAd)`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing.S12};
  right: ${({ theme }) => theme.spacing.S12};

  svg {
    color: ${({ theme }) => theme.colors.WHITE};
  }
`;

const Body = styled.div`
  background-color: ${({ theme }) => theme.colors.WHITE};
  padding: ${({ theme }) => theme.spacing.S4};

  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .variant-ENHANCED & {
    padding: ${({ theme }) => theme.spacing.S8};
  }

  ${media.medium} {
    padding: ${({ theme }) => theme.spacing.S8};

    .variant-XS & {
      padding: ${({ theme }) => theme.spacing.S4};
    }

    .variant-ENHANCED.orientation-HORIZONTAL & {
      padding: ${({ theme }) =>
        `${theme.spacing.M24} ${theme.spacing.M16} ${theme.spacing.M16}`};
    }
  }
`;

// Information Components: Price, PPM etc
const Information = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`;

const InfoItems = styled.div`
  display: flex;
  align-items: flex-end;
`;

const Info = styled.div<{ variant: CardVariations }>`
  margin-left: ${({ theme }) => theme.spacing.S8};
  border-left: 1px solid ${({ theme }) => theme.colors.GREY_LIGHT};
  padding-left: ${({ theme }) => theme.spacing.S8};

  ${media.medium} {
    margin-left: ${({ theme, variant }) =>
      variant === 'SMALL' || variant === 'XS'
        ? theme.spacing.S8
        : theme.spacing.M16};
    padding-left: ${({ theme, variant }) =>
      variant === 'SMALL' || variant === 'XS'
        ? theme.spacing.S8
        : theme.spacing.M16};
  }
`;

const InfoLabel = styled.p`
  ${({ theme }) => theme.fontSize.M14};
  color: ${({ theme }) => theme.colors.GREY_DARK};

  .variant-SIMPLE & {
    ${media.smallOnly} {
      display: none;
    }
  }

  .variant-XS & {
    display: none;
  }
`;

const InfoText = styled.p`
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  ${({ theme }) => theme.fontSize.L18};

  .variant-XS & {
    ${({ theme }) => theme.fontSize.B12};
  }

  .variant-SIMPLE & {
    ${({ theme }) => theme.fontSize.B12};
    ${media.medium} {
      ${({ theme }) => theme.fontSize.L18};
    }
  }
`;

const InfoSubText = styled.span`
  ${({ theme }) => theme.fontSize.B12};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
`;

const PriceLabelWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PriceDrop = styled.p`
  ${({ theme }) => theme.fontSize.B10};
  color: ${({ theme }) => theme.colors.RED};
  margin-left: ${({ theme }) => theme.spacing.S4};
  position: relative;
  display: inline-block;

  ${media.medium} {
    ${({ theme }) => theme.fontSize.S12};
  }

  .variant-SIMPLE & {
    ${media.smallOnly} {
      margin-left: 0;
    }
  }

  .variant-XS & {
    ${({ theme }) => theme.fontSize.B10};
    margin-left: 0;
  }

  &:after {
    position: absolute;
    content: '';
    left: 0;
    top: 50%;
    right: 0;
    border-top: 1px solid;
    border-color: inherit;
    transform: rotate(-12.2deg);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  .orientation-HORIZONTAL & {
    ${media.medium} {
      width: 320px;
    }
  }
`;

const SMediaCount = styled(MediaCount)`
  position: absolute;
  left: ${({ theme }) => theme.spacing.S8};
  bottom: ${({ theme }) => theme.spacing.S8};
`;

const PictureWrapper = styled.div`
  .orientation-HORIZONTAL & {
    ${media.medium} {
      width: 320px;
    }
  }
`;

type TPicture = { width: number; height: number };
const Picture = styled.picture<TPicture>`
  display: block;
  position: relative;
  padding-bottom: ${({ width, height }) => (height / width) * 100}%;

  img {
    width: 100%;
    height: auto;

    // NEW
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const PlaceholderPicture = styled.div<{
  image: string;
  width: number;
  height: number;
}>`
  padding-bottom: ${({ width, height }) => (height / width) * 100}%;
  background: url('${({ image }) => image}') no-repeat 50% 50%;
  background-size: cover;
`;

export const Tags = styled.ul`
  margin-bottom: ${({ theme }) => theme.spacing.S4};
  display: flex;
  flex-wrap: wrap-reverse;
  direction: rtl;
  justify-content: flex-end;
`;

const Tag = styled.li<{ order: number }>`
  margin-bottom: ${({ theme }) => theme.spacing.S8};
  margin-right: ${({ theme }) => theme.spacing.S8};
  order: ${({ order }) => order};
`;

function Card({
  variant = 'SIMPLE',
  orientation = 'HORIZONTAL',
  onSave,
  userSaved,
  imgSrc,
  imgSrcWebp,
  noImgSrc,
  imgAlt,
  price,
  currency,
  oldPrice,
  highlight,
  highlightText,
  title,
  info,
  infoLabel = 'Price',
  keyInfoList,
  mediaCount = 0,
  tags,
  header,
  priceOnRequest = false,
  renderAdditionalHeaderInfo,
  shouldTruncateHeader = false,
  smallTruncationHeaderWidth = 0,
}: CardProps) {
  const isVertical = Boolean(orientation === 'VERTICAL');
  const isEnhanced = Boolean(variant === 'ENHANCED');
  const isSimple = Boolean(variant === 'SIMPLE');
  const shouldShowHeader = Boolean(
    header && header.title && header.logo && isEnhanced && !isVertical,
  );
  const shouldShowSaveAdOverImage = Boolean(
    isSimple || variant === 'XS' || variant === 'SMALL',
  );

  const isEnhancedOrSimple = isEnhanced || isSimple;

  return (
    <CardWrapper className={`variant-${variant} orientation-${orientation}`}>
      {shouldShowHeader && (
        <Header>
          <InfoTitle
            title={header!.title as string}
            listItems={[header?.subTitle]}
            illustration={header!.logo}
            variant="SMALL"
            shouldTruncate={shouldTruncateHeader}
            smallTruncationWidth={smallTruncationHeaderWidth}
          />
          {renderAdditionalHeaderInfo && <>{renderAdditionalHeaderInfo}</>}
        </Header>
      )}
      <MainContent highlight={highlight} showBorder={!!shouldShowHeader}>
        {highlight && highlightText && (
          <Highlight highlight={highlight}>{highlightText}</Highlight>
        )}
        <ImageContainer>
          <PictureWrapper>
            {imgSrc ? (
              <Picture {...ImageDimensions}>
                {imgSrcWebp && <source type="image/webp" srcSet={imgSrcWebp} />}
                <img src={imgSrc} alt={imgAlt} {...ImageDimensions} />
              </Picture>
            ) : (
              <PlaceholderPicture image={noImgSrc} {...ImageDimensions} />
            )}
          </PictureWrapper>
          {shouldShowSaveAdOverImage && onSave && (
            <SimpleSaveAd
              toggleSave={onSave}
              isAdSaved={userSaved}
              hideText={true}
              variant="OVERLAY"
            />
          )}
          {isEnhanced && mediaCount > 0 && (
            <SMediaCount mediaCount={mediaCount} />
          )}
        </ImageContainer>
        <Body>
          <div>
            <Title>{title}</Title>
            <KeyInfoList>
              {isEnhancedOrSimple &&
                keyInfoList &&
                keyInfoList.map((info, index) => (
                  <KeyInfoItem key={index} className={`ki-${index + 1}`}>
                    {info}
                  </KeyInfoItem>
                ))}
            </KeyInfoList>
          </div>
          {isEnhancedOrSimple && tags && (
            <Tags>
              {tags.map((tag, index) => (
                <Tag key={index} order={index + 1}>
                  {tag}
                </Tag>
              ))}
            </Tags>
          )}
          <Information>
            <InfoItems>
              {priceOnRequest ? (
                <div>
                  <InfoLabel>{infoLabel}</InfoLabel>
                  <InfoText>{PriceTypes.ON_REQUEST}</InfoText>
                </div>
              ) : price ? (
                <div>
                  <PriceLabelWrapper>
                    <InfoLabel>{infoLabel}</InfoLabel>
                    {oldPrice && (
                      <PriceDrop>
                        {currency === 'EUR' ? '€' : '£'}
                        {oldPrice}
                      </PriceDrop>
                    )}
                  </PriceLabelWrapper>
                  <InfoText>
                    {currency === 'EUR' ? '€' : '£'}
                    {price}
                  </InfoText>
                </div>
              ) : (
                <div>
                  <InfoLabel>{infoLabel}</InfoLabel>
                  <InfoText>No Price</InfoText>
                </div>
              )}
              {info &&
                info.map((infoData, index) => (
                  <Info variant={variant} key={index}>
                    <InfoLabel>{infoData.title}</InfoLabel>
                    <InfoText>
                      {infoData.content}
                      {infoData.subText && (
                        <InfoSubText> {infoData.subText}</InfoSubText>
                      )}
                    </InfoText>
                  </Info>
                ))}
            </InfoItems>
            {isEnhanced && onSave && (
              <SaveAd
                toggleSave={onSave}
                isAdSaved={userSaved}
                hideText={true}
              />
            )}
          </Information>
        </Body>
      </MainContent>
    </CardWrapper>
  );
}

export { Card };
